import ErrorMessage from "../error/ErrorMessage";
import Loader from "../loader/Loader";
import { ReactNode } from "react";
import { RemoteDataSimple } from "../../types";
import { errorMessageOrDefault } from "../../utils/error";

type RemoteDataProps<TData, TErr> = {
  query: RemoteDataSimple<TData, TErr>;
  whenLoading?: () => ReactNode;
  whenError?: (err: unknown) => ReactNode;
  whenData: ({
    data,
    isFetching,
  }: {
    data: TData;
    isFetching: boolean;
  }) => ReactNode;
};

/**
 * Helper component when working with a `RemoteDataSimple` instance like a `useQuery` result.
 * Allows you to declaratively render different states.
 *
 * Useful anytime you find yourself manually checking things in JSX like
 *
 * ```
 * if (query.isLoading) ...
 * if (query.error) ...
 * if (query.data) ...
 * ```
 *
 * Instead you can use this component:
 *
 * @example
 *
 * // loading and error states have defaults so this is the minimal example
 * <RemoteData
 *  query={query}
 *  whenData={({ data }) => <div>{data.value}</div>}
 * />
 *
 * // you can provide your own loading and error handlers though
 * <RemoteData
 *  query={query}
 *  whenLoading={() => <MyCustomLoader />}
 *  whenError={(err) => <MyCustomError error={err} />}
 *  whenData={({ data }) => <div>{data.value}</div>}
 * />
 */
const RemoteData = <TData, TErr>({
  query,
  whenLoading = () => <Loader />,
  whenError = (err) => (
    <ErrorMessage>{errorMessageOrDefault(err)}</ErrorMessage>
  ),
  whenData,
}: RemoteDataProps<TData, TErr>) => {
  if (query.data) {
    return whenData({ data: query.data, isFetching: query.isFetching });
  }

  if (query.error) {
    return whenError(query.error);
  }

  if (query.isLoading) {
    return whenLoading();
  }

  return null;
};

export default RemoteData;
